/**
 * Mobile shell — drawer + bottom tab bar that wrap the whole app on
 * narrow viewports (<lg). The desktop top-nav bar in Layout.js is hidden
 * by `lg:flex` / `hidden` switches; this component handles everything below.
 *
 * Theme-aware: relies on the same CSS variables as the rest of the app
 * (--c-background, --c-text-primary, etc.). Switches automatically when
 * PreferencesContext toggles `data-theme`.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Home, BookOpen, GraduationCap, FileText, User,
  Menu, Search, X, LogOut, ChevronDown, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAssignmentBadges } from '../context/AssignmentBadgesContext';
import { buildDrawerSections } from './mobileNavSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'home',     label: 'Accueil', icon: Home,           path: '/' },
  { id: 'quran',    label: 'Coran',   icon: BookOpen,       path: '/quran' },
  { id: 'cours',    label: 'Cours',   icon: GraduationCap,  path: '/cours' },
  { id: 'devoirs',  label: 'Devoirs', icon: FileText,       path: '/devoirs', authOnly: true },
  { id: 'profile',  label: 'Profil',  icon: User,           path: '/profile-tab' },
];

/* Match the active tab against current pathname (longest-prefix wins). */
function matchTab(pathname) {
  const ranked = TABS
    .map((t) => ({ id: t.id, len: t.path === '/' ? 0 : (pathname.startsWith(t.path) ? t.path.length : -1) }))
    .filter((x) => x.len >= 0)
    .sort((a, b) => b.len - a.len);
  if (pathname === '/') return 'home';
  return ranked[0]?.id || null;
}

export const BottomTabBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const badges = useAssignmentBadges?.() || {};
  const totalUnseen = (badges.receivedUnseenCount || 0) + (badges.submittedUnseenCount || 0);
  const activeId = matchTab(location.pathname);

  const onTabClick = (tab) => {
    if (tab.authOnly && !user) {
      navigate('/login');
      return;
    }
    if (tab.id === 'profile') {
      navigate(user ? '/settings' : '/login');
      return;
    }
    navigate(tab.path);
  };

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="mobile-bottom-tabbar"
    >
      <div className="grid grid-cols-5 px-1 pt-2 pb-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeId === tab.id;
          const showBadge = tab.id === 'devoirs' && (totalUnseen > 0);
          return (
            <button
              key={tab.id}
              onClick={() => onTabClick(tab)}
              data-testid={`mobile-tab-${tab.id}`}
              className={`relative flex flex-col items-center gap-1 py-1.5 rounded-xl transition-colors ${
                isActive ? 'text-primary' : 'text-text-muted'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={isActive ? 2.4 : 1.8} />
              <span className="text-[10px] font-medium">{tab.label}</span>
              {showBadge && (
                <span className="absolute top-0.5 right-[28%] w-4 h-4 text-[9px] bg-danger text-white
                                 rounded-full flex items-center justify-center font-semibold">
                  {totalUnseen > 9 ? '9+' : totalUnseen}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

/* ===================================================================== */

const NavLinkButton = ({ it, location, navigate, indent = false }) => {
  // Strip query/hash from the link path before comparing with current pathname.
  const cleanPath = (it.path || '').split('?')[0].split('#')[0];
  const search = (it.path || '').includes('?') ? `?${it.path.split('?')[1]}` : '';
  const pathMatches = location.pathname === cleanPath
    || (cleanPath !== '/' && location.pathname.startsWith(cleanPath));
  const isActive = pathMatches
    && (!search || location.search === search);
  return (
    <button
      onClick={() => navigate(it.path)}
      data-testid={`drawer-link-${it.path.replace(/[^a-z0-9]/gi, '-').replace(/^-|-$/g, '')}`}
      className={`w-full flex items-center gap-3 ${indent ? 'pl-8' : 'pl-4'} pr-4 py-2.5 text-sm
                  border-l-[3px] transition-colors ${
                    isActive
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-transparent text-text-primary hover:bg-surface-elevated'
                  }`}
    >
      {it.icon ? <it.icon className="w-4 h-4 opacity-80" /> : null}
      <span>{it.label}</span>
    </button>
  );
};

const DrawerSection = ({ section, first, location, navigate }) => {
  // Accordion state — kept open if user is currently on one of the items.
  const hasActiveItem = section.items.some(
    (it) => location.pathname === it.path
      || (it.path !== '/' && location.pathname.startsWith(it.path)),
  );
  const startsOpen = !section.collapsible || section.defaultOpen || hasActiveItem;
  const [open, setOpen] = useState(startsOpen);

  // Re-open when route changes into this section
  useEffect(() => {
    if (hasActiveItem) setOpen(true);
  }, [hasActiveItem]);

  const wrapClass = first ? '' : 'mt-2 pt-2 border-t border-border';
  const Icon = section.icon;

  // Flat section (no title) — render items only
  if (!section.title) {
    return (
      <div className={wrapClass} data-testid="drawer-section-flat">
        {section.items.map((it) => (
          <NavLinkButton key={it.path} it={it} location={location} navigate={navigate} />
        ))}
      </div>
    );
  }

  const slug = section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <div className={wrapClass} data-testid={`drawer-section-${slug}`}>
      {section.collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 pt-2 pb-1 text-[10px]
                     uppercase tracking-wider text-text-muted font-semibold
                     hover:text-text-primary transition-colors"
          data-testid={`drawer-section-toggle-${slug}`}
          aria-expanded={open}
        >
          <span className="flex items-center gap-2">
            {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
            {section.title}
          </span>
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      ) : (
        <h4 className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-text-muted font-semibold flex items-center gap-2">
          {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
          {section.title}
        </h4>
      )}
      {(open || !section.collapsible) && (
        <div data-testid={`drawer-section-body-${slug}`}>
          {section.items.map((it) => (
            <NavLinkButton
              key={it.path}
              it={it}
              location={location}
              navigate={navigate}
              indent={section.collapsible}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const MobileDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [memberships, setMemberships] = useState([]);

  // Close on route change
  useEffect(() => { if (open) onClose(); /* eslint-disable-next-line */ }, [location.pathname]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Fetch memberships once for role-based sections (lightweight: only on auth + open)
  useEffect(() => {
    if (!user || !open) return;
    const t = localStorage.getItem('noor_albayan_token');
    axios.get(`${API_URL}/api/madrassas/me`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then((r) => setMemberships(r.data?.items || []))
      .catch(() => setMemberships([]));
  }, [user, open]);

  const initials = (user?.name || user?.email || '?').slice(0, 1).toUpperCase();
  const sections = buildDrawerSections({ user, memberships });

  return (
    <>
      <div
        onClick={onClose}
        className={`lg:hidden fixed inset-0 z-50 bg-black/55 transition-opacity ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        data-testid="mobile-drawer-backdrop"
      />
      <aside
        className={`lg:hidden fixed top-0 bottom-0 left-0 z-50 w-[280px] bg-surface border-r border-border
                    flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'}`}
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          // Make sure the logout footer is never hidden behind the Android
          // gesture-nav bar (env(safe-area-inset-bottom) can be 0 on some
          // WebView setups, hence the absolute floor of 56px).
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px) + 16px, 56px)',
        }}
        data-testid="mobile-drawer"
        role="dialog"
        aria-label="Menu de navigation"
      >
        {/* Profile area */}
        <div className="flex items-center gap-3 p-4 border-b border-border pt-2">
          <div className="w-11 h-11 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-base">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            {user ? (
              <>
                <div className="text-sm font-medium text-text-primary truncate">{user.name || user.email}</div>
                <div className="text-[11px] text-text-muted">
                  {user.role === 'admin'
                    ? 'Administrateur'
                    : memberships.some((m) => m.role === 'teacher' && m.status === 'approved')
                      ? 'Professeur'
                      : memberships.some((m) => m.role === 'admin' && m.status === 'approved')
                        ? 'Admin madrassa'
                        : user.signup_role === 'parent'
                          ? 'Parent'
                          : user.signup_role === 'teacher'
                            ? 'Professeur (sans classe)'
                            : user.signup_role === 'madrassa_admin'
                              ? 'Admin madrassa (sans madrassa)'
                              : 'Étudiant'}
                </div>
              </>
            ) : (
              <Link to="/login" className="text-sm text-primary hover:underline" data-testid="drawer-login-link">
                Connexion · S'inscrire
              </Link>
            )}
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary p-2 mr-1" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav items grouped by sections (accordion for collapsible) */}
        <div className="flex-1 overflow-y-auto py-2">
          {sections.map((section, sIdx) => (
            <DrawerSection
              key={sIdx}
              section={section}
              first={sIdx === 0}
              location={location}
              navigate={navigate}
            />
          ))}
        </div>

        {/* Footer logout (only when logged in) */}
        {user && (
          <div className="border-t border-border py-2">
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-danger hover:bg-danger/10"
              data-testid="drawer-logout"
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

/* ===================================================================== */

export const MobileHeader = ({ onOpenDrawer }) => {
  const navigate = useNavigate();
  return (
    <header
      className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-background/85 backdrop-blur-xl border-b border-border"
      style={{ paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))' }}
      data-testid="mobile-header"
    >
      <div className="flex items-center justify-between px-3 h-14">
        <button
          onClick={onOpenDrawer}
          className="w-10 h-10 ml-0 flex items-center justify-center text-text-primary hover:bg-surface-elevated rounded-xl transition-colors shrink-0"
          data-testid="mobile-burger-btn"
          aria-label="Ouvrir le menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        <Link to="/" className="flex items-center gap-2 flex-shrink-0 mx-auto" data-testid="mobile-logo-link">
          <img src="/logo-192.png" alt="Noor El Bayen" className="w-9 h-9 min-w-[36px] min-h-[36px] rounded-lg shrink-0 bg-primary/20 p-0.5" />
          <h1 className="font-serif text-sm text-text-primary whitespace-nowrap" dir="rtl">نور البيان</h1>
        </Link>

        <button
          onClick={() => navigate('/search')}
          className="w-10 h-10 mr-0 flex items-center justify-center text-text-primary hover:bg-surface-elevated rounded-xl transition-colors shrink-0"
          data-testid="mobile-search-btn"
          aria-label="Rechercher"
        >
          <Search className="w-5 h-5" />
        </button>
      </div>
    </header>
  );
};

/* ===================================================================== */

export const MobileShell = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <>
      <MobileHeader onOpenDrawer={() => setDrawerOpen(true)} />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <main
        className="lg:hidden pt-14 pb-20"
        style={{
          paddingTop: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {children}
      </main>
      <BottomTabBar />
    </>
  );
};
